import { c } from 'react-compiler-runtime';
import { clsx } from 'clsx';
import React, { useContext, useEffect } from 'react';
import useIsomorphicLayoutEffect from '../utils/useIsomorphicLayoutEffect.js';
import { CheckboxGroupContext } from '../CheckboxGroup/CheckboxGroupContext.js';
import classes from './Checkbox.module.css.js';
import sharedClasses from './shared.module.css.js';
import { jsx } from 'react/jsx-runtime';
import { useProvidedRefOrCreate } from '../hooks/useProvidedRefOrCreate.js';
import Box from '../Box/Box.js';

/**
 * An accessible, native checkbox component
 */
const Checkbox = /*#__PURE__*/React.forwardRef((t0, ref) => {
  const $ = c(47);
  let checked;
  let className;
  let defaultChecked;
  let disabled;
  let indeterminate;
  let onChange;
  let required;
  let rest;
  let sxProp;
  let validationStatus;
  let value;
  if ($[0] !== t0) {
    ({
      checked,
      className,
      defaultChecked,
      indeterminate,
      disabled,
      onChange,
      sx: sxProp,
      required,
      validationStatus,
      value,
      ...rest
    } = t0);
    $[0] = t0;
    $[1] = checked;
    $[2] = className;
    $[3] = defaultChecked;
    $[4] = disabled;
    $[5] = indeterminate;
    $[6] = onChange;
    $[7] = required;
    $[8] = rest;
    $[9] = sxProp;
    $[10] = validationStatus;
    $[11] = value;
  } else {
    checked = $[1];
    className = $[2];
    defaultChecked = $[3];
    disabled = $[4];
    indeterminate = $[5];
    onChange = $[6];
    required = $[7];
    rest = $[8];
    sxProp = $[9];
    validationStatus = $[10];
    value = $[11];
  }
  const checkboxRef = useProvidedRefOrCreate(ref);
  const checkboxGroupContext = useContext(CheckboxGroupContext);
  let t1;
  if ($[12] !== checkboxGroupContext || $[13] !== onChange) {
    t1 = e => {
      checkboxGroupContext.onChange && checkboxGroupContext.onChange(e);
      onChange && onChange(e);
    };
    $[12] = checkboxGroupContext;
    $[13] = onChange;
    $[14] = t1;
  } else {
    t1 = $[14];
  }
  const handleOnChange = t1;
  const t2 = indeterminate ? false : checked;
  const t3 = required ? "true" : "false";
  const t4 = validationStatus === "error" ? "true" : "false";
  let t5;
  if ($[15] !== checkboxRef || $[16] !== defaultChecked || $[17] !== disabled || $[18] !== handleOnChange || $[19] !== required || $[20] !== rest || $[21] !== t2 || $[22] !== t3 || $[23] !== t4 || $[24] !== value) {
    t5 = {
      type: "checkbox",
      disabled,
      ref: checkboxRef,
      checked: t2,
      defaultChecked,
      required,
      "aria-required": t3,
      "aria-invalid": t4,
      onChange: handleOnChange,
      value,
      name: value,
      ...rest
    };
    $[15] = checkboxRef;
    $[16] = defaultChecked;
    $[17] = disabled;
    $[18] = handleOnChange;
    $[19] = required;
    $[20] = rest;
    $[21] = t2;
    $[22] = t3;
    $[23] = t4;
    $[24] = value;
    $[25] = t5;
  } else {
    t5 = $[25];
  }
  const inputProps = t5;
  let t6;
  if ($[26] !== checkboxRef || $[27] !== indeterminate) {
    t6 = () => {
      if (checkboxRef.current) {
        checkboxRef.current.indeterminate = indeterminate || false;
      }
    };
    $[26] = checkboxRef;
    $[27] = indeterminate;
    $[28] = t6;
  } else {
    t6 = $[28];
  }
  let t7;
  if ($[29] !== checkboxRef || $[30] !== checked || $[31] !== indeterminate) {
    t7 = [indeterminate, checked, checkboxRef];
    $[29] = checkboxRef;
    $[30] = checked;
    $[31] = indeterminate;
    $[32] = t7;
  } else {
    t7 = $[32];
  }
  useIsomorphicLayoutEffect(t6, t7);
  let t8;
  if ($[33] !== checkboxRef || $[34] !== indeterminate) {
    t8 = () => {
      const {
        current: checkbox
      } = checkboxRef;
      if (!checkbox) {
        return;
      }
      if (indeterminate) {
        checkbox.setAttribute("aria-checked", "mixed");
      } else {
        checkbox.setAttribute("aria-checked", checkbox.checked ? "true" : "false");
      }
    };
    $[33] = checkboxRef;
    $[34] = indeterminate;
    $[35] = t8;
  } else {
    t8 = $[35];
  }
  useEffect(t8);
  if (sxProp) {
    let t9;
    if ($[36] !== className) {
      t9 = clsx(className, sharedClasses.Input, classes.Checkbox);
      $[36] = className;
      $[37] = t9;
    } else {
      t9 = $[37];
    }
    let t10;
    if ($[38] !== inputProps || $[39] !== sxProp || $[40] !== t9) {
      t10 = /*#__PURE__*/jsx(Box, {
        as: "input",
        ...inputProps,
        className: t9,
        sx: sxProp
      });
      $[38] = inputProps;
      $[39] = sxProp;
      $[40] = t9;
      $[41] = t10;
    } else {
      t10 = $[41];
    }
    return t10;
  }
  let t9;
  if ($[42] !== className) {
    t9 = clsx(className, sharedClasses.Input, classes.Checkbox);
    $[42] = className;
    $[43] = t9;
  } else {
    t9 = $[43];
  }
  let t10;
  if ($[44] !== inputProps || $[45] !== t9) {
    t10 = /*#__PURE__*/jsx("input", {
      ...inputProps,
      className: t9
    });
    $[44] = inputProps;
    $[45] = t9;
    $[46] = t10;
  } else {
    t10 = $[46];
  }
  return t10;
});
Checkbox.displayName = 'Checkbox';

export { Checkbox as default };
